import GS from '../../grafsync/grafsync';
import { ENTITLEMENTS } from '../../application-core/constants';
import Avatar from '@material-ui/core/Avatar';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import AssignmentReturnedIcon from '@material-ui/icons/AssignmentReturned';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import HelpIcon from '@material-ui/icons/Help';
import NewReleasesIcon from '@material-ui/icons/NewReleases';
import PersonIcon from '@material-ui/icons/Person';
import PropTypes from 'prop-types';
import React from 'react';
import LangConfirmationField from '../LangConfirmationField';
import { globalStyles as styles } from '../style/gstheme';
import { STATUS_ACCEPTED, STATUS_PENDING, STATUS_REJECTED } from '../utils/constants';
import withGS from '../withGShoc';
import AssignUserDialog from './AssignUserDialog';
import TaskResourceDisplay from './TaskResourceDisplay';
import { computeTypeFromType, getLabelDisplayLangFromLabel, getLabelTextFromLabel } from '../utils/utils';

const APPROVED = STATUS_ACCEPTED;
const PENDING = STATUS_PENDING;
const REJECTED = STATUS_REJECTED;

class TaskDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      showAssignUserDialog: false,
      disabled: false
    };
  }

  setToApproved = () => {
    this.props.data.status !== undefined ?
      this.props.onUpdate('status', APPROVED, null, 0)
      :
      this.props.onAdd('status', APPROVED);
  };

  showAssignUser = () => this.setState({ showAssignUserDialog: true });

  cancelAssignUser = () => this.setState({ showAssignUserDialog: false });

  assignUser = (user, text, lang, notify) => {
    if (user === "All") user = undefined;
    this.setState({ showAssignUserDialog: false, user });
    if (!(this.props.data.assignedUser === undefined && user === undefined)) {
      if (user && notify) {
        // Data should have been loaded by task manager
        const resourceData = GS.data(this.props.data.isTaskFor, this.props.graph, false);
        if (resourceData) {
          const isIn = resourceData.inScheme || resourceData.inDataset || resourceData.visibility;
          const componentsList = Array.isArray(isIn) ? isIn : [isIn];
          const component = this.props.components.find(c => componentsList.includes(c.id) &&
            (resourceData.inScheme || resourceData.inDataset || resourceData.visibility));
          if (component) {
            const computedType = computeTypeFromType(resourceData.type);
            const labelProp = resourceData.label || resourceData.prefLabel || resourceData.literalForm;
            const displayLang = getLabelDisplayLangFromLabel(labelProp, this.props.defaultLang, this.props.defaultLang);
            const label = getLabelTextFromLabel(labelProp, resourceData.id, displayLang, resourceData.id);
            const notification = {
              "user": user,
              "target": resourceData.id,
              "targetClass": computedType,
              "component": component.id,
              "instructions": "You have been assigned a task for a resource (" + label + ")."
            };
            GS.setNotification(notification);
          }
        }
      }
      this.props.data.assignedUser !== undefined ?
        user === undefined ?
          this.props.onDelete('assignedUser', this.props.data.assignedUser)
          :
          this.props.onUpdate('assignedUser', user)
        :
        this.props.onAdd('assignedUser', user);
    }
    this.props.onChange();
  };

  // We need to bubble up from the resource data in certain updates
  monitorLinkUpdate = (iri) => {
    const data = GS.data(iri, this.props.graph, false);
    let disabled = false;
    const isIn = data.inScheme || data.inDataset || data.visibility;
    if (!isIn || (isIn && isIn.length === 0) || data.deprecated === true || data.pendingDeletion === true) {
      disabled = true;
    }
    this.setState({
      disabled,
      ...(disabled) && { showAssignUserDialog: false }
    });
  };

  render() {

    const { data, classes, defaultLang, projectUsers, graph, components, viewLang, project, projectType, entitlements, user } = this.props;
    const { assignedUser, assignedNote, isTaskFor, status } = data;
    const { showAssignUserDialog, disabled } = this.state;

    const foundAssignedUser = projectUsers.find(user => user.value === assignedUser);
    const assigned = foundAssignedUser ?
      foundAssignedUser ?
        foundAssignedUser.label
        :
        "User not available"
      :
      "Unassigned";

    const usedStatus = status ?
      Array.isArray(status) ?
        status[0]
        :
        status
      :
      null;

    const statusText = usedStatus ?
      usedStatus === PENDING ?
        "Pending"
        :
        usedStatus === APPROVED ?
          "Completed"
          :
          "Rejected"
      :
      "Uncategorised";

    const isDeleted = data.pendingDeletion;
    const canEdit = (entitlements.includes(ENTITLEMENTS.EDIT_TASK) || entitlements.includes(ENTITLEMENTS.CREATE_TASK))
      && usedStatus !== APPROVED && usedStatus !== REJECTED && !disabled;

    return (
      <React.Fragment>
        <TableRow
          classes={{
            root: classes.stripedRow
          }}
          style={{ ...(isDeleted) && { backgroundColor: '#DD2C00' } }}>
          <TaskResourceDisplay
            id={isTaskFor}
            updateEvent={this.monitorLinkUpdate}
            deleteEvent={this.monitorLinkUpdate}
            components={components}
            projectType={projectType}
            project={project}
            handleNotAvailable={true}
            suppressLoading={true}
            graph={graph}
            viewLang={viewLang}
            defaultLang={defaultLang} />
          <TableCell component="td" className={classes.tableCell}>
            <div style={{ paddingTop: 4 }}>
              <Chip
                data-test="status"
                color="primary"
                avatar={
                  <Avatar style={{ backgroundColor: 'inherit' }}>
                    {(!status || status === undefined) &&
                      <HelpIcon fontSize="small" />
                    }
                    {usedStatus === PENDING &&
                      <NewReleasesIcon fontSize="small" />
                    }
                    {usedStatus === APPROVED &&
                      <AssignmentTurnedInIcon fontSize="small" />
                    }
                    {usedStatus === REJECTED &&
                      <AssignmentReturnedIcon fontSize="small" />
                    }
                  </Avatar>
                }
                clickable={false}
                label={statusText}
                className={classes.statusChip}
              />
            </div>
          </TableCell>
          <TableCell component="td" className={classes.tableCell} align='left'>
            <div style={{ paddingTop: 4 }}>
              {assignedUser && !isDeleted &&
                <Chip
                  data-test="assignee"
                  color="secondary"
                  avatar={
                    <Avatar style={{ backgroundColor: 'inherit' }}>
                      <AssignmentIndIcon fontSize="small" />
                    </Avatar>
                  }
                  clickable={false}
                  label={assigned}
                  className={classes.statusChip}
                />
              }
            </div>
          </TableCell>
          <TableCell component="td" className={classes.tableCell} align='left'>
            {assignedNote && Object.keys(assignedNote).length > 0 && !isDeleted &&
              <LangConfirmationField
                dataTest="instructions"
                focus={false}
                canBeDeleted={false}
                multiline={true}
                text={assignedNote[Object.keys(assignedNote)[0]]}
                lang={Object.keys(assignedNote)[0]}
                isReadOnly={true} />
            }
          </TableCell>
          <TableCell component="td" className={classes.tableCell} align='right'>
            <div style={{ paddingTop: 4, marginRight: 12 }}>
              {!isDeleted && canEdit && (!foundAssignedUser || assignedUser === user) &&
                <Tooltip placement="right" title='Assign'>
                  <IconButton data-test="assign" size="small" onClick={this.showAssignUser}>
                    <PersonIcon />
                  </IconButton>
                </Tooltip>
              }
              {!isDeleted && canEdit && showAssignUserDialog &&
                <AssignUserDialog
                  onAssignUser={this.assignUser}
                  exclude={assignedUser || "All"}
                  allowInstructions={false}
                  defaultLang={defaultLang}
                  projectUsers={projectUsers}
                  onCancel={this.cancelAssignUser}
                />
              }
            </div>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
}

TaskDisplay.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.object.isRequired,
  components: PropTypes.array.isRequired,
  graph: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
  entitlements: PropTypes.array,
  user: PropTypes.string,
  projectType: PropTypes.string.isRequired,
  projectUsers: PropTypes.array.isRequired,
  viewLang: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired
};

export default withStyles(styles)(withGS(TaskDisplay, ["ALL"], null, {}, true));